import useAdvanceTable from 'hooks/useAdvanceTable';
import React, { useEffect, useState } from 'react'
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Card, Col, Row, Form, Button, Spinner } from 'react-bootstrap';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import { Link, useNavigate } from 'react-router-dom';
import paths from 'routes/paths';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import axios from 'helpers/api';
import { toast } from 'react-toastify';
import moment from 'moment';
import { createIframeReportBlobUrl } from 'helpers/iframeReportPreview';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';

const columns = [
    {
        accessorKey: 'groupname',
        header: 'Group Name',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        },
        cell: ({ row: { original } }) => {
            const facilitylistlookUp = paths.facilitylistlookUp;
            return <Link to={{ pathname: `${facilitylistlookUp}/${original.id}` }} state={{
                name: original.groupname
            }} >
                {original.groupname}</Link>
        }
    },
    {
        accessorKey: 'peltask',
        header: 'Peltask',
        meta: {
            headerProps: { className: 'text-900' },
        },
        cell: ({ row: { original } }) => {
            return original.peltask ? (<FontAwesomeIcon icon="check" className="ms-1 text-success" />) : (<FontAwesomeIcon icon="check" className="ms-1 opacity-50" />)
        }
    },
    {
        accessorKey: 'mapyourmeds',
        header: 'MAP YOUR MEDS',
        meta: {
            headerProps: { className: 'text-900' },
        },
        cell: ({ row: { original } }) => {
            return original.mapyourmeds ? (<FontAwesomeIcon icon="check" className="ms-1 text-success" />) : (<FontAwesomeIcon icon="check" className="ms-1  opacity-50" />)
        }
    },
    {
        accessorKey: 'pelpak',
        header: 'Pelpak',
        meta: {
            headerProps: { className: 'text-900' },
        },
        cell: ({ row: { original } }) => {
            return original.pelpak ? (<FontAwesomeIcon icon="check" className="ms-1 text-success" />) : (<FontAwesomeIcon icon="check" className="ms-1 opacity-50" />)
        }
    },
    {
        accessorKey: 'deviceapprove',
        header: 'Device Approve',
        meta: {
            headerProps: { className: 'text-900' },
        },
        cell: ({ row: { original } }) => {
            return original.pelpak ? (original.deviceapprove ? (<FontAwesomeIcon icon="check" className="ms-1 text-success" />) : (<FontAwesomeIcon icon="check" className="ms-1 opacity-50" />)) : (<FontAwesomeIcon icon="check" className="ms-1 opacity-50" />)
        }
    },
    {
        accessorKey: 'dayprograms',
        header: 'Day Program',
        meta: {
            headerProps: { className: 'text-900' },
        },
        cell: ({ row: { original } }) => {
            return original.dayprograms ? (<FontAwesomeIcon icon="check" className="ms-1 text-success" />) : (<FontAwesomeIcon icon="check" className="ms-1 opacity-50" />)
        }
    },
    {
        accessorKey: 'sharedliving',
        header: 'Shared Living',
        meta: {
            headerProps: { className: 'text-900' },
        },
        cell: ({ row: { original } }) => {
            return original.sharedliving ? (<FontAwesomeIcon icon="check" className="ms-1 text-success" />) : (<FontAwesomeIcon icon="check" className="ms-1 opacity-50" />)
        }
    },
];



const FacilityLookUp = () => {
    const [group, setGroup] = useState([]);
    const [iframeSrc, setIframeSrc] = useState(null);
    const [isPrinting, setIsPrinting] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const navigate = useNavigate();
    const reportUrl = `${process.env.REACT_APP_SERVER_URL}/reports/groups-access-report`;

    // Free the last report's blob URL when it is replaced or the page closes.
    useEffect(() => () => iframeSrc && URL.revokeObjectURL(iframeSrc), [iframeSrc]);

    // PDF: the server returns the report as HTML; loading it in the hidden
    // iframe opens the print dialog, where it can be saved as PDF.
    const handleDownloadPdf = async () => {
        setIsPrinting(true);
        try {
            const tokenStr = localStorage.getItem('accessToken');
            const response = await axios.get(reportUrl, {
                headers: { Authorization: `Bearer ${tokenStr}` }
            });
            if (typeof response.data !== 'string') {
                toast.error(response.data?.message || 'Unable to generate Groups Access Report');
                return;
            }
            const { blobUrl } = await createIframeReportBlobUrl(response.data);
            setIframeSrc(blobUrl);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error generating Groups Access Report');
        } finally {
            setIsPrinting(false);
        }
    };

    const handleExportCsv = async () => {
        setIsExporting(true);
        try {
            const tokenStr = localStorage.getItem('accessToken');
            const response = await axios.get(`${reportUrl}?is_print=csv`, {
                headers: { Authorization: `Bearer ${tokenStr}` }
            });
            if (typeof response.data !== 'string') {
                toast.error(response.data?.message || 'Unable to export Groups Access Report');
                return;
            }
            const disposition = response.headers['content-disposition'];
            const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || '');
            const filename = match
                ? match[1].trim()
                : `groups-access-report-${moment().format('MM-DD-YYYY')}.csv`;
            const url = URL.createObjectURL(
                new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            toast.success('Groups Access Report exported successfully');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error exporting Groups Access Report');
        } finally {
            setIsExporting(false);
        }
    };
    useEffect(() => {
        fetchFacilityData()
    }, [])


    async function fetchFacilityData() {
        try {
            let tokenStr = localStorage.getItem("accessToken");
            const { data } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/facilities/get-all-groups`, { headers: { 'Authorization': `Bearer ${tokenStr}` } });
            let results = [];
            let objData = data?.data;

            Object.values(objData).forEach(value => results.push({
                id: value.id,
                groupname: value.name,
                peltask: value.is_peltask_active,
                mapyourmeds: value.is_mym_active,
                pelpak: value.is_mobile_app_active,
                deviceapprove: value.enable_device_approve,
                dayprograms: value.is_dayprogram_active,
                sharedliving: value.is_sharedliving_active
            }));
            setGroup(results);
        } catch (error) {
            navigate("/error")
        }
    }

    const table = useAdvanceTable({
        data: group,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    const breadcrumbItems = [
        { name: 'Dashboard', href: '/main', isActive: false },
        { name: 'Groups&Facility', href: paths.faciliityLookUp, isActive: true }
    ];

    return (
        <>
            {group.length > 0 ? 
            <>
            <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
            <AdvanceTableProvider {...table}>
                <Card className="mb-3">
                    <Card.Body className="p-3">
                        <Row className='mt-3 justify-content-between'>
                            <Col xs="4" className="mt-4">
                                <AdvanceTableSearchBox placeholder="Search..." />
                            </Col>
                            <Col xs="auto" className="mt-4 d-flex align-items-center gap-2">
                                <Button
                                    variant="outline-primary"
                                    onClick={handleDownloadPdf}
                                    disabled={isPrinting}
                                    className="d-inline-flex align-items-center"
                                >
                                    {isPrinting ? (
                                        <Spinner as="span" animation="border" size="sm" className="me-1" />
                                    ) : (
                                        <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                                    )}
                                    {isPrinting ? 'Generating...' : 'Download PDF'}
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    onClick={handleExportCsv}
                                    disabled={isExporting}
                                    className="d-inline-flex align-items-center"
                                >
                                    {isExporting ? (
                                        <Spinner as="span" animation="border" size="sm" className="me-1" />
                                    ) : (
                                        <FontAwesomeIcon icon={faFileCsv} className="me-1" />
                                    )}
                                    {isExporting ? 'Exporting...' : 'Export CSV'}
                                </Button>
                            </Col>
                        </Row>
                        <div style={{ height: 0, width: 0, overflow: 'hidden' }}>
                            {iframeSrc && (
                                <iframe
                                    src={iframeSrc}
                                    style={{ width: '100%', height: '500px', border: 'none' }}
                                    title="Groups Access Report"
                                />
                            )}
                        </div>
                        <Row>
                            <Col></Col>
                            <Col></Col>
                        </Row>
                        <Row>
                            <Col xs="12" className="mb-1 mt-3">
                                <AdvanceTable
                                    headerClassName="bg-200 text-nowrap align-middle text-center"
                                    rowClassName="text-nowrap align-middle text-center"
                                    tableProps={{
                                        size: 'sm',
                                        striped: true,
                                        className: 'fs-10 mb-0 overflow-hidden'
                                    }}
                                />
                            </Col>
                        </Row>
                    </Card.Body>
                    <Card.Footer>
                        <div className="mt-3">
                            <AdvanceTableFooter
                                rowInfo
                                navButtons
                                rowsPerPageSelection
                            />
                        </div>
                    </Card.Footer>
                </Card>
            </AdvanceTableProvider>
            </> : <ShimmerUI/>
            }
        </>
    )
}

export default FacilityLookUp
